// src/components/Posts/CreatePost.jsx
import { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, FormControl, InputLabel, Select, MenuItem,
  CircularProgress, Grid, IconButton, Tooltip, TextField, Button, Stack,
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import VideocamIcon from '@mui/icons-material/Videocam';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import AttachFileIcon from '@mui/icons-material/AttachFile';        // 📎 첨부파일 아이콘

import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { createPost } from '../../api/posts';
import api from '../../api/axios';
import { useUser } from '../../contexts/UserContext';
import PostForm from './PostForm';
import VoteForm from './VoteForm';

export default function CreatePost() {
  /* ─────────────────────────── 기본 설정 */
  const { userInfo, refreshUserInfo } = useUser();
  const navigate = useNavigate();
  const { search } = useLocation();
  const [searchParams] = useSearchParams();
  const categoryIdFromURL = searchParams.get('category');

  const isLoggedIn = !!userInfo?.username;
  const formRef = useRef(null);
  // ① 아이콘 버튼 공통 스타일 (가로·세로 28px + 아래 여백) 
  const iconSx = {
    p: 0,            // 패딩 제거 
    width: 28,
    height: 28,
    mb: 0.5,         // 아이콘 아래 margin-bottom 4px 
  };

  /* ─────────────────────────── 상태 */
  const [title, setTitle] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedOptions, setSelectedOptions] = useState({
    anonymous_option: 'none',
    secret_option: 'none',
  });

  const [loadingCategories, setLoadingCategories] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showVoteForm, setShowVoteForm] = useState(false);
  const [voteData, setVoteData] = useState(null);
  const [attachedFile, setAttachedFile] = useState(null);   // 📎 첨부파일

  /* ─────────────────────────── 카테고리 불러오기 */
  useEffect(() => {
    if (!isLoggedIn) return;

    api.get('/api/boards/categories/')
      .then(res => {
        const cats = res.data.categories;
        setCategories(cats);

        // URL 로 받은 category 기본 선택
        if (categoryIdFromURL) {
          const found = cats.find(c => c.id === Number(categoryIdFromURL));
          if (found) {
            setSelectedCategory(found.id);
            setSelectedOptions({
              anonymous_option: found.anonymous_option || 'none',
              secret_option: found.secret_option || 'none',
            });
          }
        }
      })
      .catch(() => alert('카테고리 불러오기 실패!'))
      .finally(() => setLoadingCategories(false));
  }, [isLoggedIn, categoryIdFromURL]);

  /* ─────────────────────────── input 핸들러 */
  const handleCategoryChange = e => {
    const id = e.target.value;
    const cat = categories.find(c => c.id === id);
    setSelectedCategory(id);
    if (cat) {
      setSelectedOptions({
        anonymous_option: cat.anonymous_option || 'none',
        secret_option: cat.secret_option || 'none',
      });
    }
  };
  const handleFileSelect = e => setAttachedFile(e.target.files?.[0] || null);
  const handleVideoSelect = async file =>
    file && (await formRef.current?.insertVideo(file));

  /* ─────────────────────────── 제출 */
  const handleSubmit = async () => {
    const content = formRef.current?.getContent() || '';
    if (!selectedCategory) return alert('카테고리를 선택하세요!');
    if (!title.trim()) return alert('제목을 입력하세요!');
    if (!content.trim() || content === '<p></p>')
      return alert('본문을 입력하세요!');

    const isAnonymous = selectedOptions.anonymous_option === 'force';
    const isSecret = selectedOptions.secret_option === 'force';

    const fd = new FormData();
    fd.append('title', title);
    fd.append('content', content);
    fd.append('category', selectedCategory);
    fd.append('is_anonymous', isAnonymous);
    fd.append('is_secret', isSecret);
    if (attachedFile) fd.append('attachment', attachedFile);

    if (voteData?.has_vote) {
      fd.append('has_vote', 'true');
      fd.append('vote_question', voteData.vote_question);
      voteData.vote_options.forEach((opt, i) =>
        fd.append(`vote_options[${i}]`, opt));
      fd.append('vote_deadline', voteData.vote_deadline);
      fd.append('allow_multiple', voteData.allow_multiple);
      fd.append('is_anonymous_vote', voteData.is_anonymous_vote);
      fd.append('allow_add_option', voteData.allow_add_option);
    }

    setIsSubmitting(true);
    createPost(fd)
      .then(({ data }) => {
        const newId = data.id ?? data.post_id ?? data.pk;
        refreshUserInfo();
        alert('게시글 생성 성공!');
        navigate(`/main/posts/${newId}${search}`, { replace: true });
      })
      .catch(() => alert('게시글 생성 실패!'))
      .finally(() => setIsSubmitting(false));
  };

  if (!isLoggedIn) {
    return <Typography sx={{ p: 4 }}>로그인 후 게시글 작성이 가능합니다.</Typography>;
  }

  /* ─────────────────────────── UI */
  return (
    <Box sx={{ p: 4, maxWidth: 700, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>[ 게시글 작성 ]</Typography>

      {/* ---------- 카테고리 선택 ---------- */}
      <FormControl fullWidth margin="normal" required>
        <InputLabel id="cat-label">카테고리 선택</InputLabel>
        {loadingCategories ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <Select labelId="cat-label" value={selectedCategory}
            label="카테고리 선택" onChange={handleCategoryChange}>
            {categories.map(({ id, emoji, name }) => (
              <MenuItem key={id} value={id}>{emoji} {name}</MenuItem>
            ))}
          </Select>
        )}
      </FormControl>

      {/* ---------- 제목 ---------- */}
      <TextField fullWidth label="제목" margin="normal"
        value={title} onChange={e => setTitle(e.target.value)} />

      {/* 📱 모바일 아이콘 4개 */}
      <Stack
        direction="row"
        spacing={2}
        sx={{
          my: 1,             // 위·아래 같은 여백
          pb: 1,             // 필요하면 조정
          display: { xs: 'flex', md: 'none' },
          justifyContent: 'flex-start',
          alignItems: 'center',
        }}
      >
        {/* 1. 사진 */}
        <input id="img-inline" hidden type="file" accept="image/*"
          onChange={e => formRef.current?.insertImage(e.target.files?.[0])} />
        <label htmlFor="img-inline" style={{ cursor: 'pointer' }}>
          <Tooltip title="본문 이미지 삽입">
            <IconButton sx={iconSx} component="span">
              <PhotoCameraIcon sx={{ fontSize: 26 }} />
            </IconButton>
          </Tooltip>
        </label>

        {/* 2. 영상 */}
        <input id="vid-inline" hidden type="file" accept="video/*"
          onChange={e => handleVideoSelect(e.target.files?.[0])} />
        <label htmlFor="vid-inline" style={{ cursor: 'pointer' }}>
          <Tooltip title="영상 삽입">
            <IconButton sx={iconSx} component="span">
              <VideocamIcon sx={{ fontSize: 26 }} />
            </IconButton>
          </Tooltip>
        </label>

        {/* 3. 첨부파일 */}
        <input id="attach-inline" hidden type="file"
          onChange={e => handleFileSelect(e.target.files?.[0])} />
        <label htmlFor="attach-inline" style={{ cursor: 'pointer' }}>
          <Tooltip title="첨부파일 업로드">
            <IconButton sx={iconSx} component="span">
              <AttachFileIcon sx={{ fontSize: 26 }} />
            </IconButton>
          </Tooltip>
        </label>

        {/* 4. 투표 */}
        <Tooltip title={showVoteForm ? '투표 접기' : '투표 추가'}>
          <IconButton
            sx={iconSx}
            component="span"
            color={showVoteForm ? 'primary' : 'default'}
            onClick={() => setShowVoteForm(p => !p)}
          >
            <HowToVoteIcon sx={{ fontSize: 26 }} />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* ---------- 💻 데스크톱 input 영역 ---------- */}
      <Grid container spacing={2}
        sx={{ mt: 1, alignItems: 'center', display: { xs: 'none', md: 'flex' } }}>
        {/* 이미지 input */}
        <Grid item xs={12} md={6}>
          <Typography sx={{ mb: 1 }}>본문 이미지 삽입</Typography>
          <input type="file" accept="image/*" style={{ width: '100%' }}
            onChange={e => formRef.current?.insertImage(e.target.files?.[0])} />
        </Grid>

        {/* 영상 input */}
        <Grid item xs={12} md={6}>
          <Typography sx={{ mb: 1 }}>본문 영상 삽입</Typography>
          <input type="file" accept="video/*" style={{ width: '100%' }}
            onChange={e => handleVideoSelect(e.target.files?.[0])} />
        </Grid>

        {/* 첨부파일 input */}
        <Grid item xs={12}>
          <Typography sx={{ mt: 2, mb: 1 }}>첨부파일 (선택)</Typography>
          <input type="file" onChange={handleFileSelect} />
          {attachedFile && (
            <Typography variant="caption" sx={{ mt: 0.5 }}>
              선택된 파일: {attachedFile.name}
            </Typography>
          )}
        </Grid>
      </Grid>

      {/* ---------- 투표 폼 ---------- */}
      {(showVoteForm || window.matchMedia('(min-width:960px)').matches) &&
        <VoteForm onChange={setVoteData} />}

      {/* ---------- 본문 에디터 ---------- */}
      <PostForm ref={formRef}
        disabled={isSubmitting || loadingCategories} />

      {/* ---------- 제출 버튼 ---------- */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button variant="contained" onClick={handleSubmit}
          disabled={isSubmitting || loadingCategories}>
          작성 완료
        </Button>
      </Box>
    </Box>
  );
}
